import { get, isEmpty } from "lodash"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as ProfileCreators } from "model/profileModal"
import { getEndDate } from "selectors/dates"
import { classificationsQuery } from "helpers/classification"
import { getIsOpPro } from "modules/account-root/selectors"
import ErrorPage from "components/404"
import { MODELS_QUERY, MODEL_METADATA_QUERY } from "resources/models"
import { BASKETS_QUERY } from "resources/baskets"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"
import { FORECAST_LIST_QUERY } from "resources/forecasts"
import { getRecentInstrumentsQueryUrl } from "resources/instruments"
import { getRecentInstruments } from "selectors/recentInstruments"
import { RECENT_INSTRUMENTS_RESOURCE } from "../../../sagas/app/recentInstruments"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { FAVORITE_FACTORS_TYPE } from "./model"
import { getFullLinks, getSubLinks, getControls, getIsNavigationDisabled } from "./selectors/links"
import { getLatestPortfolios, getIsActive, getFullPortfolioBenchmark, getPortfolioName, getPortfolioHasData, getPortfolioId, getSelectedModelId, getSelectedModelName, getNoRestrictionModels, getModelSupportsComposites, getUnsupportedCurrencyWarning } from "./selectors/portfolios"
import { getSearchResults, computeDefaultSelectedBase, getBaseHistory, getSelectedBase } from "./selectors/base"
import { getHasEnoughBaseModelCoverage, getBaseCoverageStartDate } from "./selectors/coverage"
import { getSearchValue, getBaseDropdownVisible, getModifiedScreens, getSecurity, getFactorId } from "./selectors"
import { getPortfolioDates } from "./selectors/model"
import { getForecasts } from "./selectors/forecast"
import { getLatestDate, getEarliestDate } from "./selectors/dates"
import { getContextualMenuItems } from "./selectors/menu"
import { getRawPortfolioBetaInstrument } from "./selectors/beta-instrument"

export default createModule({
  view,
  chromeGate: true,
  externalProps: state => ({
    portfolioId: state.global.selected.portfolio,
    portfolioUUID: getPortfolioId(state),
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    security: getSecurity(state),
    factorId: getFactorId(state),
    modelId: getSelectedModelId(state),
    latestDate: getLatestDate(state),
    earliestDate: getEarliestDate(state),
    selectedBase: getSelectedBase(state),
    isActive: getIsActive(state),
    baseCoverageStartDate: getBaseCoverageStartDate(state),
    endDate: getEndDate(state),
    isOpPro: getIsOpPro(state),
    betaInstrument: getRawPortfolioBetaInstrument(state),
    recentInstruments: getRecentInstruments(state),
  }),
  required: ["account", "portfolioId", "isOpPro"],
  data: [{
    resource: "portfolio",
    url: ({ account, portfolioId }) => `/api/${account}/p/${portfolioId}`,
  }, {
    resource: "portfolioBetaInstrument",
    passthrough: true,
    url: ({ portfolioId, isOpPro }) => {
      if (!portfolioId || isOpPro) { return null }

      return {
        id: `portfolioBetaInstrument-${portfolioId}`,
        query: `query ($portfolioId: String!) {
            portfolio(id: $portfolioId) {
              betaInstrument {
                securityId {
                  mic
                  ticker
                  figi
                  bloombergTicker
                  internalId
                }
                resource {
                  type
                  alias
                }
                indexId
                benchmarkId
              }
            }
          }`,
        variables: {
          portfolioId,
        },
      }
    },
  }, {
    resource: "portfolioBetaInstrumentDescriptors",
    passthrough: true,
    url: ({ account, modelId, betaInstrument, endDate }) => {
      if (!modelId || !endDate || !betaInstrument || !betaInstrument.securityId) { return null }
      return ({
        id: `portfolioBetaInstrument-${modelId}-${endDate}-${JSON.stringify(betaInstrument.securityId)}`,
        url: `/api/${account}/m/${modelId}/security/descriptors?date=${endDate}`,
        payload: {
          securityId: betaInstrument.securityId,
        },
      })
    },
  }, {
    resource: "portfolioModelMeta",
    url: ({ account, portfolioId, modelId }) => {
      if (!modelId) {
        return null
      }
      return `/api/${account}/m/${modelId}/r/portfolio/${portfolioId}`
    },
  }, {
    resource: "userFavoriteFactors",
    passthrough: true,
    url: ({ accountId, modelId }) => {
      if (!modelId || !accountId) {
        return null
      }

      return {
        id: `userFavoriteFactors-${accountId}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [{
            type: FAVORITE_FACTORS_TYPE,
            key: `${modelId}`,
            accountId,
          }],
        },
      }
    },
  }, {
    resource: "portfolioUserPreferences",
    url: ({ accountId, modelId, portfolioUUID }) => {
      if (!modelId || !portfolioUUID) {
        return null
      }

      return ({
        id: `portfolioUserPreferences-${accountId}${modelId}${portfolioUUID}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            { type: "viewType" },
            {
              type: "portfolioLastModelUsed",
              accountId,
              key: portfolioUUID,
            }, {
              type: "portfolioBenchmarkHistory",
              key: `${portfolioUUID}:${modelId}`,
              accountId,
            }, {
              type: "analyzeTrendsContributors",
              key: portfolioUUID,
              accountId,
            }, {
              type: "analyzeTrends",
              key: portfolioUUID,
              accountId,
            }, {
              type: "lastSelectedPeriod",
              key: portfolioUUID,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    resource: "portfolios",
    as: null,
    url: ({ account }) => `/api/${account}/portfolios`,
  },
  MODELS_QUERY,
  {
    resource: "benchmarksAndPortfolios",
    as: null,
    url: ({ modelId }) => {
      if (!modelId) {
        return null
      }

      return ({
        id: `benchmarksAndPortfolios-${modelId}`,
        query: `query ($modelId: String!) {
            model (id: $modelId) {
              benchmarks { id, name, availableFrom }
            }
            portfolios { id, availableFrom }
          }`,
        variables: { modelId },
      })
    },
  }, {
    resource: "indices",
    as: null,
    url: () => ({
      id: "indices",
      query: `query {
        indexes { id name description availableFrom }
      }`,
    }),
  }, {
    resource: "dates",
    as: null,
    url: ({ modelId }) => {
      if (!modelId) {
        return null
      }

      return {
        id: modelId,
        query: "query ($modelId: String!) { model (id: $modelId) { availability { currentDate, factorsStartDate, securitiesStartDate } } }",
        variables: { modelId },
      }
    },
  }, BASKETS_QUERY, {
    resource: "modeledDates",
    as: null,
    url: ({ modelId, latestDate, earliestDate }) => {
      if (!modelId || !latestDate || !earliestDate) {
        return null
      }

      return {
        id: `${modelId}${latestDate}${earliestDate}`,
        query: "query ($modelId: String!, $from: Date!, $to: Date!) { model (id: $modelId) { availability { dates(from: $from, to: $to) } } }",
        variables: { modelId, from: earliestDate, to: latestDate },
      }
    },
  }, MODEL_METADATA_QUERY, {
    resource: "portfolioExperiments",
    as: null,
    url: ({ portfolioId }) => {
      if (!portfolioId) {
        return null
      }

      return ({
        id: `portfolioExperiments-${portfolioId}`,
        query: `query ($portfolioId: String!) {
            portfolio(id: $portfolioId){
              experiments{ id, name, description, availableFrom, type, lastUpdated }
            }
          }`,
        variables: { portfolioId },
      })
    },
  }, {
    resource: "baseCoverage",
    passthrough: true,
    url: ({ modelId, isActive, selectedBase, baseCoverageStartDate, endDate, earliestDate }) => {
      if (!modelId || !isActive || !selectedBase || selectedBase.type !== "PORTFOLIO" || !earliestDate) {
        return null
      }

      return ({
        id: `baseCoverage-${isActive ? "active" : "absolute"}-${selectedBase.id}${modelId}`,
        query: `query ($basePortfolioId: String! $from:Date! $to:Date! $modelId: String!) {
          model(id: $modelId){
            simulation(positionSet: {id: $basePortfolioId type:PORTFOLIO} from: $from to: $to) {
              coverage {
                summary {
                  percentGmvAvailable
                }
              }
            }
          }
        }`,
        variables: {
          basePortfolioId: selectedBase.id,
          from: baseCoverageStartDate,
          to: endDate,
          modelId,
        },
      })
    },
  }, {
    ...CONTENT_SETS_METADATA_QUERY,
    as: null,
  }, {
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  },
  {
    ...FORECAST_LIST_QUERY,
    as: null,
  },
  {
    resource: "styleExposureAggregationPreferences",
    url: ({ accountId, modelId, portfolioShortId }) => {
      if (!accountId || !portfolioShortId || !modelId) {
        return null
      }

      return ({
        id: `styleExposureAggregationPreferences-${accountId}${portfolioShortId}${modelId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [
            {
              type: "styleExposureAggregation",
              key: `${portfolioShortId}${modelId}`,
              accountId,
            },
          ],
        },
      })
    },
  }, {
    resource: "tagCategories",
    url: ({ portfolioId }) => {
      return ({
        id: `tagCategories-${portfolioId}`,
        query: `query ($portfolioId: String!) {
            portfolio(id: $portfolioId){
              tagCategories {
                id
                name
              }
            }
          }`,
        variables: { portfolioId },
      })
    },
  }, {
    resource: "attributeCategories",
    url: ({ portfolioId }) => {
      return ({
        id: `attributeCategories-${portfolioId}`,
        query: `query ($portfolioId: String!) {
            portfolio(id: $portfolioId){
              positionAttributeCategories {
                id
                name
              }
            }
          }`,
        variables: { portfolioId },
      })
    },
  }, {
    resource: "etfs",
    as: null,
    url: ({ account, modelId }) => (modelId ? `/api/${account}/m/${modelId}/etfs` : null),
  }, {
    resource: "accountResearchTopics",
    url: ({ isOpPro }) => {
      if (isOpPro) {
        return null
      }

      return {
        id: "account-research-topics",
        query: "query { researchTopics { id, name, description, alias, lastUpdated, referenceInstrument { type, securityId { ticker, mic, country }, portfolioId } } }",
      }
    },
  }, {
    resource: "userFavoriteResources",
    url: ({ accountId }) => ({
      id: `userFavoriteResources-${accountId}`,
      url: "/api/users/me/preferences",
      payload: {
        items: [{ type: "favoriteResources", accountId }],
      },
    }),
  }, {
    resource: "resourcePermission",
    url: ({ portfolioId }) => ({
      id: `resource-permission-${portfolioId}`,
      query: `query ($resourceType: ResourceType!, $resourceId: IdOrShortIdOrAlias!) {
          resource(type: $resourceType, id: $resourceId) {
            id
            permissionLevel
          }
        }`,
      variables: {
        resourceType: "PORTFOLIO",
        resourceId: portfolioId,
      },
    }),
  }, {
    resource: "userDefaultMetricUnits",
    url: ({ accountId }) => {
      if (!accountId) {
        return null
      }

      return {
        id: `userDefaultMetricUnits-${accountId}`,
        url: "/api/users/me/preferences",
        payload: {
          items: [{
            type: "defaultMetricUnits",
            key: accountId,
            accountId,
          }],
        },
      }
    },
  }, {
    resource: RECENT_INSTRUMENTS_RESOURCE,
    // This query conflicts with a unified search query on the same page, so we only call it if we have no data
    url: ({ account, modelId, recentInstruments }) => isEmpty(recentInstruments) ? getRecentInstrumentsQueryUrl(account, modelId) : null,
  }],
  mapStateToProps: (state, props) => ({
    portfolioName: getPortfolioName(state),
    portfolioHasData: getPortfolioHasData(state),
    portfolioBenchmark: getFullPortfolioBenchmark(state),
    modelName: getSelectedModelName(state),
    links: getFullLinks(state),
    subLinks: getSubLinks(state),
    controls: getControls(state),
    latestPortfolios: getLatestPortfolios(state),
    searchValue: getSearchValue(state),
    baseHistory: getBaseHistory(state),
    searchResults: getSearchResults(state, props),
    baseDropdownVisible: getBaseDropdownVisible(state),
    defaultSelectedBase: computeDefaultSelectedBase(state),
    navigationDisabled: getIsNavigationDisabled(state),
    modifiedScreens: getModifiedScreens(state),
    hasEnoughBaseCoverage: getHasEnoughBaseModelCoverage(state),
    noRestrictionModels: getNoRestrictionModels(state),
    sessionExpired: get(state, "user.sessionExpired"),
    forecasts: getForecasts(state, props),
    portfolioDates: getPortfolioDates(state, props),
    menuItems: getContextualMenuItems(state, props),
    modelSupportsComposites: getModelSupportsComposites(state),
    unsupportedCurrencyWarning: getUnsupportedCurrencyWarning(state),
  }),
  error: ErrorPage,
  actions: {
    ...Creators,
    openDialog: DialogCreators.open,
    closeSecurityProfile: ProfileCreators.closeSecurityProfile,
    closeFactorProfile: ProfileCreators.closeFactorProfile,
  },
})
